import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, List, Tag, Space, Modal, Form, Input, Select, DatePicker, Divider, Alert } from 'antd';
import type { AlertProps, SpaceProps, ModalProps } from 'antd';
import { PlusOutlined, MessageOutlined, PhoneOutlined, MailOutlined, SettingOutlined, SyncOutlined } from '@ant-design/icons';
import api from '../utils/axios';
import { Communication } from '@/types/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CommunicationsProps {
  claimId: string;
}

const Communications: React.FC<CommunicationsProps> = ({ claimId }) => {
  const [communications, setCommunications] = useState<Communication[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [emailIntegrationVisible, setEmailIntegrationVisible] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCommunications();
  }, [claimId]);

  const fetchCommunications = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching communications for claim:', claimId);
      console.log('Auth token available:', !!localStorage.getItem('token'));
      
      const response = await api.get(`/claims/${claimId}/communications`);
      console.log('Communications API response:', response.data);
      
      // Handle the API response structure
      const data = response.data;
      if (data.success && Array.isArray(data.data)) {
        setCommunications(data.data as Communication[]);
        console.log('Successfully loaded communications:', data.data.length);
      } else if (Array.isArray(data)) {
        setCommunications(data as Communication[]);
        console.log('Successfully loaded communications (direct array):', data.length);
      } else {
        console.warn('Unexpected API response structure:', data);
        setCommunications([]);
      }
    } catch (error: any) {
      console.error('Error fetching communications:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Set user-friendly error message
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to view communications.');
      } else if (error.response?.status === 404) {
        setError('Communications not found for this claim.');
      } else {
        setError(`Failed to load communications: ${error.response?.data?.message || error.message}`);
      }
      setCommunications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('Creating communication with values:', values);
      const response = await api.post(`/claims/${claimId}/communications`, {
        ...values,
        created_by: 'current-user-id', // This should come from auth context
      });
      console.log('Communication created successfully:', response.data);
      setSuccess('Communication created successfully!');
      setModalVisible(false);
      form.resetFields();
      fetchCommunications();
    } catch (error: any) {
      console.error('Error creating communication:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in again.');
      } else if (error.response?.status === 403) {
        setError('Access denied. You do not have permission to create communications.');
      } else {
        setError(`Failed to create communication: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const handleSyncEmails = async (provider: 'gmail' | 'outlook') => {
    setSyncing(true);
    setError(null);
    try {
      console.log('Attempting to sync emails from:', provider);
      console.log('Claim ID:', claimId);
      console.log('Token in localStorage:', localStorage.getItem('token'));
      
      const response = await api.post(`/${provider}/claims/${claimId}/sync`);
      console.log('Sync response:', response.data);
      const { syncedCount } = (response.data as any).data;
      setSuccess(`Successfully synced ${syncedCount} emails from ${provider === 'gmail' ? 'Gmail' : 'Outlook'}`);
      fetchCommunications(); // Refresh the communications list
    } catch (error: any) {
      console.error('Sync error details:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setError(`Failed to sync emails from ${provider === 'gmail' ? 'Gmail' : 'Outlook'}: ${error.response?.data?.message || error.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'EMAIL':
        return <MailOutlined />;
      case 'PHONE_CALL':
        return <PhoneOutlined />;
      case 'SMS':
        return <MessageOutlined />;
      default:
        return <MessageOutlined />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT':
        return 'green';
      case 'DELIVERED':
        return 'blue';
      case 'READ':
        return 'purple';
      case 'FAILED':
        return 'red';
      case 'DRAFT':
        return 'orange';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'red';
      case 'HIGH':
        return 'orange';
      case 'NORMAL':
        return 'blue';
      case 'LOW':
        return 'green';
      default:
        return 'default';
    }
  };

  return (
    <div>
      {/* Alerts */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {success && (
        <Alert
          message="Success"
          description={success}
          type="success"
          showIcon
          closable
          onClose={() => setSuccess(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card 
        title="Communications" 
        extra={
          <Space>
            <Button 
              icon={<SettingOutlined />} 
              onClick={() => setEmailIntegrationVisible(true)}
            >
              Email Integration
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={() => setModalVisible(true)}
            >
              Add Communication
            </Button>
          </Space>
        }
      >
        {/* Email Integration Panel */}
        {emailIntegrationVisible && (
          <Card 
            title="Email Integration" 
            style={{ marginBottom: 16 }}
            extra={
              <Button 
                type="text" 
                onClick={() => setEmailIntegrationVisible(false)}
              >
                Close
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* Test Authentication Button */}
              <div>
                <Text strong>Test Authentication</Text>
                <br />
                <Space>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing user authentication...');
                        const response = await api.get('/auth/me');
                        console.log('User auth response:', response.data);
                        const userData = (response.data as any).user;
                        setSuccess(`User authenticated: ${userData?.email || 'Unknown'}`);
                      } catch (error: any) {
                        console.error('User auth error:', error);
                        setError(`User authentication failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test User Auth
                  </Button>
                  <Button 
                    size="small"
                    onClick={() => {
                      const token = localStorage.getItem('token');
                      console.log('Token in localStorage:', token ? 'EXISTS' : 'NOT FOUND');
                      console.log('Token length:', token?.length || 0);
                      if (token) {
                        setSuccess('Token found in localStorage');
                      } else {
                        setError('No token found in localStorage - please log in');
                      }
                    }}
                  >
                    Check Token
                  </Button>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing email sync endpoint...');
                        // Use the real claim ID we found
                        const testClaimId = '81aa7506-f68c-4788-97e0-f15cc0c0096b';
                        const response = await api.post(`/outlook/claims/${testClaimId}/sync`);
                        console.log('Email sync test response:', response.data);
                        setSuccess('Email sync endpoint is working!');
                      } catch (error: any) {
                        console.error('Email sync test error:', error);
                        setError(`Email sync test failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test Email Sync
                  </Button>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing Outlook status endpoint...');
                        const response = await api.get('/outlook/status');
                        console.log('Outlook status response:', response.data);
                        setSuccess('Outlook status endpoint is working!');
                      } catch (error: any) {
                        console.error('Outlook status test error:', error);
                        setError(`Outlook status test failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test Outlook Status
                  </Button>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing health endpoint...');
                        const response = await api.get('/health');
                        console.log('Health response:', response.data);
                        setSuccess('Health endpoint is working!');
                      } catch (error: any) {
                        console.error('Health test error:', error);
                        setError(`Health test failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test Health
                  </Button>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing simple API endpoint...');
                        const response = await api.get('/test');
                        console.log('Test response:', response.data);
                        setSuccess('API is working!');
                      } catch (error: any) {
                        console.error('API test error:', error);
                        setError(`API test failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test API
                  </Button>
                  <Button 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Testing Outlook test-auth endpoint...');
                        const response = await api.get('/outlook/test-auth');
                        console.log('Outlook test-auth response:', response.data);
                        setSuccess('Outlook test-auth is working!');
                      } catch (error: any) {
                        console.error('Outlook test-auth error:', error);
                        setError(`Outlook test-auth failed: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Test Outlook Auth
                  </Button>
                </Space>
              </div>
              
              <Divider />
              
              <div>
                <Text strong>Gmail Integration</Text>
                <br />
                <Space>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={() => window.open('/gmail/auth/url', '_blank')}
                  >
                    Connect Gmail
                  </Button>
                  <Button 
                    size="small"
                    loading={syncing}
                    onClick={() => handleSyncEmails('gmail')}
                  >
                    <SyncOutlined /> Sync Emails
                  </Button>
                </Space>
              </div>
              
              <Divider />
              
              <div>
                <Text strong>Outlook Integration</Text>
                <br />
                <Space>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={async () => {
                      try {
                        console.log('Getting Outlook auth URL...');
                        console.log('Token before request:', localStorage.getItem('token') ? 'EXISTS' : 'NOT FOUND');
                        const response = await api.get('/outlook/auth/url');
                        console.log('Auth URL response:', response.data);
                        const authUrl = (response.data as any).data?.authUrl;
                        if (authUrl) {
                          window.open(authUrl, '_blank');
                        } else {
                          setError('Failed to get auth URL');
                        }
                      } catch (error: any) {
                        console.error('Auth URL error:', error);
                        console.error('Error response:', error.response?.data);
                        console.error('Error status:', error.response?.status);
                        setError(`Failed to get auth URL: ${error.response?.data?.message || error.message}`);
                      }
                    }}
                  >
                    Connect Outlook
                  </Button>
                  <Button 
                    size="small"
                    loading={syncing}
                    onClick={() => handleSyncEmails('outlook')}
                  >
                    <SyncOutlined /> Sync Emails
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>
        )}

        {/* Communications Count and Status */}
        {!loading && (
          <div style={{ marginBottom: 16, padding: '8px 0' }}>
            <Space>
              <Text type="secondary">
                {communications.length > 0 
                  ? `${communications.length} communication${communications.length === 1 ? '' : 's'} found`
                  : 'No communications found'
                }
              </Text>
              {error && (
                <Text type="danger">• Error loading data</Text>
              )}
            </Space>
          </div>
        )}

        <List
          loading={loading}
          dataSource={Array.isArray(communications) ? communications : []}
          locale={{
            emptyText: error 
              ? 'Unable to load communications. Please check the error message above.'
              : 'No communications found for this claim. Use "Add Communication" to create the first one.'
          }}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '16px',
                border: '1px solid #f0f0f0',
                borderRadius: '8px',
                marginBottom: '8px',
                backgroundColor: '#fafafa'
              }}
            >
              <List.Item.Meta
                avatar={getTypeIcon(item.type)}
                title={
                  <Space>
                    <Text strong>{item.subject || `${item.type} Communication`}</Text>
                    <Tag color={getStatusColor(item.status)}>{item.status}</Tag>
                    <Tag color={getPriorityColor(item.priority)}>{item.priority}</Tag>
                    <Tag color={item.direction === 'INBOUND' ? 'blue' : 'green'}>
                      {item.direction}
                    </Tag>
                  </Space>
                }
                description={
                  <div>
                    <Text>{item.content}</Text>
                    <br />
                    <Text type="secondary">
                      {item.fromName && `${item.fromName} → ${item.toName}`}
                    </Text>
                    <br />
                    <Text type="secondary">
                      {new Date(item.created_at).toLocaleString()}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />

        <Modal
          title="Add Communication"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={null}
        >
          <Form form={form} onFinish={handleSubmit} layout="vertical">
            <Form.Item name="type" label="Type" rules={[{ required: true }]}>
              <Select>
                <Option value="EMAIL">Email</Option>
                <Option value="PHONE_CALL">Phone Call</Option>
                <Option value="SMS">SMS</Option>
                <Option value="WHATSAPP">WhatsApp</Option>
                <Option value="MEETING">Meeting</Option>
                <Option value="OTHER">Other</Option>
              </Select>
            </Form.Item>

            <Form.Item name="direction" label="Direction" rules={[{ required: true }]}>
              <Select>
                <Option value="INBOUND">Inbound</Option>
                <Option value="OUTBOUND">Outbound</Option>
                <Option value="BOTH">Both</Option>
              </Select>
            </Form.Item>

            <Form.Item name="subject" label="Subject">
              <Input />
            </Form.Item>

            <Form.Item name="content" label="Content" rules={[{ required: true }]}>
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
              <Select defaultValue="NORMAL">
                <Option value="LOW">Low</Option>
                <Option value="NORMAL">Normal</Option>
                <Option value="HIGH">High</Option>
                <Option value="URGENT">Urgent</Option>
              </Select>
            </Form.Item>

            <Form.Item name="fromName" label="From Name">
              <Input />
            </Form.Item>

            <Form.Item name="toName" label="To Name">
              <Input />
            </Form.Item>

            <Form.Item name="fromEmail" label="From Email">
              <Input type="email" />
            </Form.Item>

            <Form.Item name="toEmail" label="To Email">
              <Input type="email" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Create Communication
                </Button>
                <Button onClick={() => setModalVisible(false)}>
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default Communications; 